/* ============================================================
 * UI KIT primitives — the token-styled CoreUI replacements defined in this
 * file (cards, pills, alerts, buttons, form controls, tables, spinner). The
 * kit barrel (./index) re-exports them with every other kit component.
 *
 * Modules in the eager shell import from this file, not from the barrel: a
 * module the entry chunk reaches through the barrel's re-exports lands in the
 * entry as soon as any lazy page uses it.
 * ============================================================ */

import { forwardRef } from 'react'

const cx = (...parts) => parts.filter(Boolean).join(' ')

/* role → scale for the recipe components (pills, alerts, progress) */
const SCALE = {
  primary: 'cyan',
  info: 'blue',
  success: 'green',
  warning: 'amber',
  danger: 'red',
  secondary: 'slate',
  light: 'slate',
  dark: 'slate',
}
const scaleOf = (color) => SCALE[color] || 'slate'

/* ---- Surfaces ---- */

export function Card({ className, children, ...rest }) {
  return (
    <div
      className={cx(
        'overflow-hidden rounded-[var(--app-radius)] border border-[var(--app-border)] bg-[var(--app-surface)]',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  )
}

export function CardHeader({ className, children, ...rest }) {
  return (
    <div
      className={cx(
        'flex flex-wrap items-center justify-between gap-2 border-b border-[var(--app-border)] bg-[var(--app-surface-2)] px-4 py-2.5 font-medium',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  )
}

export function CardBody({ className, children, ...rest }) {
  return (
    <div className={cx('p-4', className)} {...rest}>
      {children}
    </div>
  )
}

/* ---- Pills & alerts (tint 3 / line 6 / ink 12 — never a solid step-9 block) ---- */

export function Badge({ color = 'secondary', className, children, ...rest }) {
  const s = scaleOf(color)
  return (
    <span
      className={cx('inline-flex items-center rounded-full px-[7px] py-px text-[11px] leading-[1.5] whitespace-nowrap', className)}
      style={{ backgroundColor: `var(--${s}-3)`, color: `var(--${s}-12)` }}
      {...rest}
    >
      {children}
    </span>
  )
}

export function Alert({ color = 'info', className, children, ...rest }) {
  const s = scaleOf(color)
  return (
    <div
      role="alert"
      className={cx('rounded-[var(--app-radius)] border p-3 text-[13px]', className)}
      style={{ backgroundColor: `var(--${s}-3)`, borderColor: `var(--${s}-6)`, color: `var(--${s}-12)` }}
      {...rest}
    >
      {children}
    </div>
  )
}

/* ---- Buttons (rule: primary = accent-9 + dark ink; no filled red) ---- */

const BUTTON_STYLES = {
  primary:
    'bg-[var(--app-accent-solid)] text-[var(--app-accent-contrast)] border border-transparent hover:bg-[var(--cyan-10)]',
  secondary:
    'bg-[var(--slate-3)] text-[var(--app-text)] border border-[var(--app-border-strong)] hover:bg-[var(--slate-4)]',
  danger:
    'bg-[var(--slate-3)] text-[var(--app-crit)] border border-[var(--app-border-strong)] hover:bg-[var(--slate-4)]',
}

// forwardRef so callers can return focus to the control that opened a sheet —
// OpsSheet's returnFocusRef needs the real node, and a spread prop is not one.
export const Button = forwardRef(function Button(
  { color = 'secondary', size, type = 'button', className, children, ...rest }, ref,
) {
  const look = BUTTON_STYLES[color] || BUTTON_STYLES.secondary
  return (
    <button
      ref={ref}
      type={type}
      className={cx(
        'inline-flex items-center justify-center gap-1 rounded-[var(--app-radius)] font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--app-accent-line)]',
        'disabled:cursor-not-allowed disabled:opacity-50',
        size === 'sm' ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-sm',
        look,
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  )
})

/* ---- Forms ---- */

export function FormSelect({ size, className, style, children, ...rest }) {
  // A numeric size is the HTML rows attribute of a list box, not this kit's
  // density switch. Swallowing it silently collapses a multi-select to one row,
  // so forward the number and let only 'sm' mean density.
  const rows = typeof size === 'number' ? size : undefined
  return (
    <select
      size={rows}
      className={cx(
        'rounded-[var(--app-radius)] border border-[var(--app-border-strong)] bg-[var(--app-surface)] text-[var(--app-text)]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--app-accent-line)]',
        size === 'sm' ? 'px-2 py-1 text-xs' : 'px-2.5 py-1.5 text-sm',
        className,
      )}
      style={style}
      {...rest}
    >
      {children}
    </select>
  )
}

export function FormInput({ size, className, ...rest }) {
  return (
    <input
      className={cx(
        'rounded-[var(--app-radius)] border border-[var(--app-border-strong)] bg-[var(--app-surface)] text-[var(--app-text)] placeholder:text-[var(--app-text-muted)]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--app-accent-line)]',
        size === 'sm' ? 'px-2 py-1 text-xs' : 'px-2.5 py-1.5 text-sm',
        className,
      )}
      {...rest}
    />
  )
}

// A multi-line text control with the same frame as FormInput. rows defaults
// to 4; resize is vertical only, so it never widens the page.
export function FormTextarea({ size, className, rows = 4, ...rest }) {
  return (
    <textarea
      rows={rows}
      className={cx(
        'w-full resize-y rounded-[var(--app-radius)] border border-[var(--app-border-strong)] bg-[var(--app-surface)] text-[var(--app-text)] placeholder:text-[var(--app-text-muted)]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--app-accent-line)]',
        size === 'sm' ? 'px-2 py-1 text-xs' : 'px-2.5 py-1.5 text-sm',
        className,
      )}
      {...rest}
    />
  )
}

export function FormLabel({ className, children, ...rest }) {
  return (
    <label className={cx('mb-1 block text-xs text-[var(--app-text-2)]', className)} {...rest}>
      {children}
    </label>
  )
}

export function FormSwitch({ id, label, checked, onChange, disabled, className, ...rest }) {
  return (
    <label
      // ops-switch: CoreUI's unlayered label { display: inline-block } beats the
      // layered inline-flex, which drops the gap between switch and text.
      className={cx('ops-switch inline-flex cursor-pointer items-center gap-2 text-[13px]', disabled && 'cursor-not-allowed opacity-50', className)}
      htmlFor={id}
    >
      {/* CoreUI's unlayered `label { display: inline-block }` beats any layered
          Tailwind display utility on the label, so the row lives on a span. */}
      <span className="inline-flex items-center gap-2">
        <span className="relative inline-flex h-4 w-7 shrink-0">
          {/* border-1, not border: Bootstrap's unlayered `.border` is !important
              and would pin the border colour. */}
          <input
            type="checkbox"
            role="switch"
            id={id}
            checked={checked}
            onChange={onChange}
            disabled={disabled}
            className={cx(
              'peer absolute inset-0 cursor-pointer appearance-none rounded-full border-1 border-[var(--app-border-strong)] bg-[var(--slate-3)] transition-colors',
              'checked:border-transparent checked:bg-[var(--app-accent-solid)]',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--app-accent-line)]',
              'disabled:cursor-not-allowed',
            )}
            {...rest}
          />
          {/* Thumb ink, not a surface step: dark mode's surface is the track's slate-3. */}
          <span className="pointer-events-none absolute left-0.5 top-0.5 h-3 w-3 rounded-full bg-[var(--app-text-muted)] transition-transform peer-checked:translate-x-3 peer-checked:bg-[var(--app-accent-contrast)]" />
        </span>
        {label && <span>{label}</span>}
      </span>
    </label>
  )
}

/* ---- Progress (track slate-3, bar = role step 9) ---- */

export function Progress({ value = 0, color = 'primary', thin, className, style, ...rest }) {
  const s = scaleOf(color)
  const pct = Math.max(0, Math.min(100, value))
  return (
    <div
      role="progressbar"
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
      className={cx('overflow-hidden rounded-full bg-[var(--slate-3)]', thin ? 'h-1' : 'h-2', className)}
      style={style}
      {...rest}
    >
      <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: `var(--${s}-9)` }} />
    </div>
  )
}

/* ---- Tables (hairline rows, quiet header, tabular numerals) ---- */

export function Table({ className, children, ...rest }) {
  return (
    <div className="overflow-x-auto">
      <table
        className={cx('w-full border-collapse text-[13px] [font-variant-numeric:tabular-nums]', className)}
        {...rest}
      >
        {children}
      </table>
    </div>
  )
}

export function THead({ className, children, ...rest }) {
  return (
    <thead className={cx('bg-[var(--app-surface-2)]', className)} {...rest}>
      {children}
    </thead>
  )
}

export function TBody({ className, children, ...rest }) {
  return (
    <tbody className={cx('[&>tr:hover]:bg-[var(--app-surface-2)]', className)} {...rest}>
      {children}
    </tbody>
  )
}

export function Tr({ className, children, ...rest }) {
  return (
    <tr className={cx('transition-colors', className)} {...rest}>
      {children}
    </tr>
  )
}

export function Th({ className, children, ...rest }) {
  return (
    <th
      className={cx(
        'border-b border-[var(--app-border-strong)] px-3 py-2 text-left font-medium text-[var(--app-text-2)]',
        className,
      )}
      {...rest}
    >
      {children}
    </th>
  )
}

export function Td({ className, children, ...rest }) {
  return (
    <td className={cx('border-b border-[var(--app-border)] px-3 py-2 align-middle', className)} {...rest}>
      {children}
    </td>
  )
}

/* ---- Spinner ---- */

export function Spinner({ size, className, ...rest }) {
  return (
    <span
      role="status"
      aria-label="Loading"
      className={cx(
        'inline-block animate-spin rounded-full border-2 border-[var(--app-border-strong)] border-t-[var(--app-accent-solid)]',
        size === 'sm' ? 'h-4 w-4' : 'h-6 w-6',
        className,
      )}
      {...rest}
    />
  )
}
